// EXEMPT
// TODO(deli): i think feature-specific settings should not b hardcoded here

import type {
  FromModel,
  IsUnion,
  ModelDefinition,
} from "@/__main__/data-model.mjs";
import {
  Mapped,
  Optional,
  Partial,
  Silent,
  Union,
  Unknown,
} from "@/__main__/data-model.mjs";
import type { GAME_SYMBOLS } from "@/app/constants.mjs";
import {
  THEME_AUBERGINE,
  THEME_BLUE,
  THEME_DARK,
  THEME_GHERKIN,
} from "@/app/constants.mjs";
import gamesList from "@/app/games.mjs";
import { SymbolGame } from "@/app/symbol-game.mjs";
import { model as CsgoPlayerModel } from "@/data-models/csgo-player.mjs";
import { FactionRE } from "@/game-eft/constants/constants.mjs";
import { LIVE_COMPARISON_OPTIONS } from "@/game-val/constants.mjs";

export const model = {
  favoriteGames: [SymbolGame as unknown as IsUnion<typeof GAME_SYMBOLS>],
  riotRegion: String,
  // The reason why these are silenced is because games can be added and removed,
  // but remain in settings.
  lastLoggedInIdByGame: Mapped({
    key: Silent(String), // key is game short name
    value: String,
  }),
  loggedInAccounts: Partial({
    apex: Mapped({
      key: String, // profileId
      value: {
        hoveredChampionApexId: String,
        username: String,
        profileId: String,
      },
    }),
    cs2: Mapped({
      key: String,
      value: CsgoPlayerModel,
    }),
    csgo: Mapped({
      key: String,
      value: CsgoPlayerModel,
    }),
    fortnite: Mapped({
      key: String,
      value: {
        accountId: String,
        name: String,
      },
    }),
    deadlock: Mapped({
      key: String,
      value: {
        avatar: String,
        name: String,
        steam_id_3: String,
        steam_id_64: String,
      },
    }),
    eft: Mapped({
      key: String,
      value: {
        accountId: String,
        id: String,
        nickname: String,
        side: Union([FactionRE.usec, FactionRE.bear]),
      },
    }),
    lol: Mapped({
      key: String,
      value: {
        region: String,
        gameName: String,
        tagLine: String,
        displayName: String,
        profileIconId: Number,
      },
    }),
    valorant: Mapped({
      key: String,
      value: {
        internalUuid: String,
        gameName: String,
        tagLine: String,
        lastPlayedAt: Date,
        level: Number,
        xp: Number,
        region: String,
        userAccountId: Optional(String),
        playerCardId: String,
      },
    }),
  }),
  lastPlayedGames: Mapped({
    key: Silent(Union(gamesList.map((i) => i.shortName))),
    value: Number,
  }),
  selectedLanguage: String,
  theme: Union([THEME_DARK, THEME_BLUE, THEME_AUBERGINE, THEME_GHERKIN]),
  disableAnimations: Boolean,
  dismissedNotifications: [Silent(String)],
  lol: {
    hasOnboardedBuilds: Boolean,
    displayPopup: Boolean,
    autoImportItems: Boolean,
    autoImportRunes: Boolean,
    autoImportSpells: Boolean,
    buildSort: String,
    prefersApexBuilds: Boolean,
    minimizeLoadingScreen: Boolean,
    tiltFreeMode: Boolean,
    queuePopup: Boolean,
    defaultFlashPlacement: Number,
    manualFlashPlacement: Number,
    overlays: Mapped({
      key: String,
      value: Unknown,
    }),
    lastQueueId: Union([Number, null] as [NumberConstructor, null]),
  },
  tft: {
    isSkinsEnabled: Boolean,
    overlays: Mapped({
      key: String,
      value: Unknown,
    }),
  },
  valorant: {
    muted: Boolean,
    comparisonTier: String,
    liveMatchComparison: Union(LIVE_COMPARISON_OPTIONS().map((a) => a.value)),
    overlays: {
      isAgentSelectEnabled: Boolean,
      isBenchmarkOverlayEnabled: Boolean,
      isCombatOverlayEnabled: Boolean,
      isLoadingScreenEnabled: Boolean,
    },
  },
  cs2: {
    overlays: {
      isBenchmarkOverlayEnabled: Boolean,
    },
  },
  eft: {
    overlays: {
      enableInventoryOverlay: Boolean,
    },
  },
  deadlock: {
    preferredMatchTab: String,
  },
  survey: {
    isSurveyDisabled: Boolean,
    linkedSurveyDismissedCount: Number,
  },
  battles: {
    hideEventPromos: Boolean,
  },
  wallet: {
    defaultPaymentMethodId: Optional(String),
  },
  ...Mapped({
    key: String,
    value: Unknown,
  }),
} satisfies ModelDefinition;

export type AppSettings = FromModel<typeof model>;
